import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import VoterList from "@/pages/VoterList";
import CallAssistant from "@/pages/CallAssistant";
import TeamPerformance from "@/pages/TeamPerformance";
import AuditLog from "@/pages/AuditLog";
import DailyReport from "@/pages/DailyReport";
import AnalyticsHub from "@/pages/AnalyticsHub";
import ComparisonTimeline from "@/pages/ComparisonTimeline";
import Priorities from "@/pages/Priorities";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import CommitteeScorecard from "@/pages/CommitteeScorecard";
import ResponsiblesManager from "@/pages/ResponsiblesManager";
import Sessions from "@/pages/Sessions";
import UserReports from "@/pages/UserReports";
import ElectionResults from "@/pages/ElectionResults";
import Radar from "@/pages/Radar";
import ContactsMatch from "@/pages/ContactsMatch";
import CommitteeMatrix from "@/pages/CommitteeMatrix";
import Efficiency from "@/pages/Efficiency";
import BranchScorecard from "@/pages/BranchScorecard";
import LayerConflicts from "@/pages/LayerConflicts";
import FullSync from "@/pages/FullSync";
import DeleteRequests from "@/pages/DeleteRequests";
import BranchPanel from "@/pages/BranchPanel";
import YetkiTracking from "@/pages/YetkiTracking";
import UserGuide from "@/pages/UserGuide";
import BallotGuide from "@/pages/BallotGuide";
import Pool from "@/pages/Pool";
import BigPicture from "@/pages/BigPicture";
import CommitteeDetail from "@/pages/CommitteeDetail";
import CommitteeAnalytics from "@/pages/CommitteeAnalytics";
import Compare from "@/pages/Compare";
import { StatusFilterProvider } from "@/context/StatusFilterContext";
import { ElectionDayProvider } from "@/context/ElectionDayContext";
import ElectionDay from "@/pages/ElectionDay";
import Scoreboard from "@/pages/Scoreboard";
import TravelPlanPage from "@/pages/TravelPlanPage";
import Blank from "@/pages/Blank";

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        Yukleniyor...
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "field_agent" ? "/arama" : "/panel"} replace />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <ElectionDayProvider>
        <StatusFilterProvider>
        <div className="App">
          <Toaster position="top-right" richColors />
          <BrowserRouter>
            <Routes>
              <Route path="/giris-2026" element={<Login />} />
              <Route path="/2026" element={<Blank gate />} />
              <Route path="/login" element={<Blank />} />
              <Route path="/izle" element={<Scoreboard />} />
              <Route path="/" element={<HomeRedirect />} />
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/panel" element={<ProtectedRoute roles={["super_admin", "committee_manager", "chairman", "branch_manager"]}><Dashboard /></ProtectedRoute>} />
                <Route path="/karne" element={<ProtectedRoute roles={["super_admin", "committee_manager", "chairman"]}><CommitteeScorecard /></ProtectedRoute>} />
                <Route path="/komite-analizleri" element={<ProtectedRoute roles={["super_admin", "committee_manager"]}><CommitteeAnalytics /></ProtectedRoute>} />
                <Route path="/sube-karnesi" element={<ProtectedRoute roles={["super_admin", "committee_manager", "branch_manager"]}><BranchScorecard /></ProtectedRoute>} />
                <Route path="/katman-farklari" element={<ProtectedRoute roles={["super_admin", "committee_manager", "branch_manager"]}><LayerConflicts /></ProtectedRoute>} />
                <Route path="/full-liste-esitleme" element={<ProtectedRoute roles={["super_admin"]}><FullSync /></ProtectedRoute>} />
                <Route path="/silme-talepleri" element={<ProtectedRoute roles={["super_admin", "committee_manager"]}><DeleteRequests /></ProtectedRoute>} />
                <Route path="/sube-panel" element={<ProtectedRoute roles={["super_admin", "committee_manager", "branch_manager"]}><BranchPanel /></ProtectedRoute>} />
                <Route path="/yetki-takip" element={<ProtectedRoute roles={["super_admin", "committee_manager", "branch_manager"]}><YetkiTracking /></ProtectedRoute>} />
                <Route path="/gunluk-rapor" element={<ProtectedRoute roles={["super_admin", "committee_manager", "branch_manager"]}><DailyReport /></ProtectedRoute>} />
                <Route path="/analizler" element={<ProtectedRoute roles={["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"]}><AnalyticsHub /></ProtectedRoute>} />
                <Route path="/analizler/:tab" element={<ProtectedRoute roles={["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"]}><AnalyticsHub /></ProtectedRoute>} />
                <Route path="/verimlilik" element={<ProtectedRoute roles={["super_admin", "committee_manager", "chairman"]}><Efficiency /></ProtectedRoute>} />
                <Route path="/secmenler" element={<VoterList />} />
                <Route path="/arama" element={<CallAssistant />} />
                <Route path="/takim" element={<ProtectedRoute roles={["super_admin", "committee_manager", "chairman"]}><TeamPerformance /></ProtectedRoute>} />
                <Route path="/gunluk" element={<ProtectedRoute roles={["super_admin", "committee_manager", "chairman"]}><AuditLog /></ProtectedRoute>} />
                <Route path="/karsilastir" element={<ComparisonTimeline />} />
                <Route path="/oncelik" element={<Priorities />} />
                <Route path="/kullanicilar" element={<ProtectedRoute roles={["super_admin", "committee_manager", "chairman"]}><Users /></ProtectedRoute>} />
                <Route path="/yetki-matrisi" element={<ProtectedRoute roles={["super_admin", "committee_manager"]}><CommitteeMatrix /></ProtectedRoute>} />
                <Route path="/sorumlular" element={<ProtectedRoute roles={["super_admin", "committee_manager"]}><ResponsiblesManager /></ProtectedRoute>} />
                <Route path="/oturumlar" element={<ProtectedRoute roles={["super_admin"]}><Sessions /></ProtectedRoute>} />
                <Route path="/kullanici-raporlari" element={<ProtectedRoute roles={["super_admin"]}><UserReports /></ProtectedRoute>} />
                <Route path="/gecmis-sonuclar" element={<ProtectedRoute roles={["super_admin"]}><ElectionResults /></ProtectedRoute>} />
                <Route path="/radar" element={<ProtectedRoute roles={["super_admin", "committee_manager", "branch_manager"]}><Radar /></ProtectedRoute>} />
                <Route path="/rehber" element={<ContactsMatch />} />
                <Route path="/havuz" element={<ProtectedRoute><Pool /></ProtectedRoute>} />
                <Route path="/buyuk-resim" element={<ProtectedRoute roles={["super_admin", "committee_manager"]}><BigPicture /></ProtectedRoute>} />
                <Route path="/komite/:cno" element={<ProtectedRoute roles={["super_admin", "committee_manager", "chairman", "branch_manager"]}><CommitteeDetail /></ProtectedRoute>} />
                <Route path="/kiyasla" element={<ProtectedRoute roles={["super_admin", "committee_manager", "chairman", "branch_manager"]}><Compare /></ProtectedRoute>} />
                <Route path="/kilavuz" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />
                <Route path="/cetele-kilavuzu" element={<ProtectedRoute><BallotGuide /></ProtectedRoute>} />
                <Route path="/cetele" element={<ProtectedRoute><ElectionDay /></ProtectedRoute>} />
                <Route path="/seyahat-plani" element={<ProtectedRoute roles={["super_admin", "committee_manager", "chairman", "branch_manager"]}><TravelPlanPage /></ProtectedRoute>} />
                <Route path="/sonuc-ekrani" element={<ProtectedRoute><Scoreboard /></ProtectedRoute>} />
                <Route path="/ayarlar" element={<ProtectedRoute roles={["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"]}><Settings /></ProtectedRoute>} />
              </Route>
              <Route path="*" element={<Blank />} />
            </Routes>
          </BrowserRouter>
        </div>
        </StatusFilterProvider>
        </ElectionDayProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
